import {
  authentication,
  createDirectus,
  createFolder,
  createItem,
  createItems,
  createUser,
  createUsers,
  deleteFile,
  deleteFiles,
  deleteItem,
  deleteItems,
  deleteUser,
  deleteUsers,
  passwordReset,
  readFolders,
  readItem,
  readItems,
  readMe,
  readUser,
  readUsers,
  realtime,
  rest,
  updateItem,
  updateItems,
  updateMe,
  updateUser,
  uploadFiles,
} from '@directus/sdk'
import { useNotificationStore } from '~/store/notification'
import { Api } from './directus/api'
import { SafeAsyncWebSocket } from './directus/safeAsyncWebSocket'

export default defineNuxtPlugin(async ({ $route, $router, $config }) => {
  const notificationStore = useNotificationStore()

  const directus = createDirectus($config.public.cmsUrl)
    .with(authentication('cookie', { credentials: 'include' }))
    .with(rest({ credentials: 'include' }))
    .with(realtime())

  directus.globals.WebSocket = SafeAsyncWebSocket

  /*
   * Coalesce concurrent refreshes: the cookie-based refresh token rotates
   * atomically on the server, so two parallel directus.refresh() calls race
   * and the loser gets a 401.
   */
  let refreshInFlight = null
  const refreshOnce = () => {
    if (!refreshInFlight) {
      refreshInFlight = directus.refresh().finally(() => {
        refreshInFlight = null
      })
    }
    return refreshInFlight
  }

  directus.globals.fetch = $fetch.create({
    async onResponseError({ response, options }) {
      // Only 401 means the token is invalid — refreshing on other errors is unsound.
      if (
        !options.isRetry &&
        response.status === 401 &&
        !response.url.includes('/auth/refresh') &&
        !response.url.includes('/auth/login')
      ) {
        try {
          const refreshResult = await refreshOnce()
          options.headers['Authorization'] = 'Bearer ' + refreshResult.access_token
          options.isRetry = true
          return
        } catch {
          // Swallow only; the switch below owns the /auth/refresh login redirect.
          return
        }
      }

      const route = $route || $router?.currentRoute?.value

      // Skip failed refresh calls on login page (used for checking if user is already autenticated)
      if (response.url.includes('/auth/refresh') && route?.path === '/auth/login') {
        return
      }

      if (!response.url.includes('/items/notifications')) {
        switch (response.status) {
          case 401:
            if (!response.url.includes('/users/me')) {
              if (!response.url.includes('/auth/login')) {
                notificationStore.set({
                  title: 'Sitzung abgelaufen',
                  text: 'Bitte melden Sie sich erneut an.',
                  type: 'error',
                })
                if (route?.fullPath) {
                  return navigateTo('/auth/login?path=' + encodeURIComponent(route.fullPath))
                } else {
                  return navigateTo('/auth/login')
                }
              }
              return Promise.reject(response) // Return the error
            }
            break
          case 403:
            if (!response.url.includes('/auth/password/reset')) {
              notificationStore.set({
                title: 'Fehlende Berechtigungen',
                text: 'Leider besitzen Sie für diese Aktion nicht die erforderlichen Berechtigungen',
                type: 'error',
              })
              response._notified = true
              return Promise.reject(response)
            }
            break
          case 400:
            if (response.url.includes('/auth/logout')) {
              return navigateTo('/auth/login')
            }
            if (
              response.url.includes('/auth/refresh') &&
              route?.path !== '/auth/forward' &&
              route?.path !== '/auth/login'
            ) {
              if (route?.path !== '/auth/logout') {
                notificationStore.set({
                  title: 'Sitzung abgelaufen',
                  text: 'Bitte melden Sie sich erneut an.',
                  type: 'error',
                })
              }
              return navigateTo('/auth/login')
            }
            {
              const errorCode = extractErrorCode(response)
              const messages = errorCode ? buildErrorMessages([{ code: errorCode }]) : []
              if (messages.length) {
                messages.forEach((message) => notificationStore.set(message))
              } else {
                notificationStore.set({
                  title: 'Vorgang fehlgeschlagen',
                  text: 'Ihre Anfrage war ungültig. Bitte überprüfen Sie Ihre Eingaben.',
                  type: 'error',
                })
              }
            }
            response._notified = true
            return Promise.reject(response)

          default: {
            const errorCode = extractErrorCode(response)
            const messages = errorCode ? buildErrorMessages([{ code: errorCode }]) : []
            if (messages.length) {
              messages.forEach((message) => notificationStore.set(message))
            } else {
              notificationStore.set({
                title: 'Vorgang fehlgeschlagen',
                text: 'Es ist ein unerwarteter Fehler aufgetreten. Bitte kontaktieren Sie einen Administrator.',
                type: 'error',
              })
            }
            // A real Error (not the raw Response) so Sentry/GlitchTip captures a stack trace
            // And a readable message, while _data keeps extractErrorCode() working.
            return Promise.reject(
              Object.assign(
                new Error(`Request failed with status ${response.status}: ${response.url}`),
                {
                  status: response.status,
                  _data: response._data,
                  response,
                  _notified: true,
                },
              ),
            )
          }
        }
      }
    },
    retry: 1,
    retryStatusCodes: [401],
  })

  const uploadFile = async (file, title, folderName = null, tags = null) => {
    let folderId = null

    try {
      if (folderName) {
        const folders = await directus.request(
          readFolders({
            filter: {
              name: {
                _eq: folderName,
              },
            },
            fields: ['id', 'name'],
          }),
        )
        if (folders.length) {
          folderId = folders[0].id
        } else {
          const folder = await directus.request(
            createFolder(
              {
                name: folderName,
              },
              {
                fields: ['id', 'name'],
              },
            ),
          )
          folderId = folder.id
        }
      }

      const formData = new FormData()
      formData.append('title', title)
      formData.append('tags', JSON.stringify(tags))
      formData.append('file', file)
      if (folderId) {
        formData.append('folder', folderId)
      }
      const fileResponse = await directus.request(uploadFiles(formData))
      return fileResponse
    } catch (error) {
      console.error('Error during file upload:', error)
      throw error
    }
  }

  const api = new Api(directus)

  return {
    provide: {
      cms: directus,
      api,
      createItem,
      createItems,
      createUser,
      createUsers,
      deleteFile,
      deleteFiles,
      deleteItem,
      deleteItems,
      deleteUser,
      deleteUsers,
      passwordReset,
      readItem,
      readItems,
      readMe,
      readUser,
      readUsers,
      updateItem,
      updateItems,
      updateMe,
      updateUser,
      uploadFile,
    },
  }
})
